import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHistory, useParams } from 'react-router';

import ProductsCveStore from 'app/store/ProductsCveStore';
import usePageMeta from 'hooks/use-page-meta';
import useSearchParams from 'hooks/use-search-params';
import useStores from 'hooks/use-stores';
import Heading from 'main/components/Heading/Heading';
import DataTable from 'pages/Search/components/DataTable';
import Layout from 'pages/Search/components/Layout';
import MonthFilter from 'pages/Search/components/MonthFilter';
import RedirectLink from 'pages/Search/components/RedirectLink';
import YearFilter from 'pages/Search/components/YearFilter';
import { ROUTES } from 'pages/Search/constants';
import { noneIf, normalizeName } from 'pages/Search/utils';

import useStyles from './styles';

const Years = () => {
    const { classes } = useStyles();
    const history = useHistory();
    const { productsCveStore } = useStores();

    const { year, month, page } = useParams<{ year?: string; month?: string; page?: string }>();
    const [{ pageSize }, setSearchParams] = useSearchParams();

    const layoutRef = useRef<{ scrollTop: () => void } | null>(null);

    const monthName = useMemo(() => {
        if (!month) return '';
        const date = new Date();
        date.setMonth(Number(month) - 1);
        return date.toLocaleString('en-US', { month: 'long' });
    }, [month]);

    const hasMetaPagination = Number(page || 1) > 1;
    const pageMeta = hasMetaPagination ? ' - Page ' + page : '';

    usePageMeta({
        title:
            year || month
                ? `Vulnerabilities by date - ${monthName || ''} ${year || ''}${pageMeta} | Vulners.com`
                : `Vulnerabilities by date${pageMeta} | Vulners.com`,
        description:
            year || month
                ? `Vulnerabilities released in ${monthName || ''} ${year || ''}${pageMeta}`
                : `Vulnerabilities released through the years${pageMeta}`
    });

    const setPath = useCallback(
        ({ year, month, page }) =>
            history.push({ pathname: ROUTES.YEARS({ year, month, page }), search: history.location.search }),
        [history]
    );

    const getYearOptionHref = useCallback(
        (year) => ROUTES.YEARS({ page: null, year, month }).concat(history.location.search),
        [history.location.search, month]
    );

    const getMonthOptionHref = useCallback(
        (month) => ROUTES.YEARS({ page: null, year, month }).concat(history.location.search),
        [history.location.search, year]
    );

    const getPaginationHref = useCallback(
        (page) => ROUTES.YEARS({ page: noneIf(page, 1), year, month }).concat(history.location.search),
        [history.location.search, month, year]
    );

    useEffect(() => {
        layoutRef.current?.scrollTop();
        productsCveStore
            .setParams({
                page: Number(page || 1),
                pageSize: Number(pageSize || 100),
                year: year ? Number(year) : 0,
                month: month ? Number(month) : 0
            })
            .loadItems();
    }, [month, page, pageSize, productsCveStore, year]);

    const tableData = useMemo(
        () => [
            ['Vendor Name', 'Product Name', 'Vulnerabilities'],
            ...productsCveStore.list.map(({ vendorName, productName, cveCount }, index) => [
                <RedirectLink
                    key={`${vendorName}-vulnerabilities-${index}`}
                    to={ROUTES.VENDOR({ page: null, vendorName, ...({ year, month } as object) })}
                    label={normalizeName(vendorName)}
                    active={Boolean(vendorName)}
                />,
                <RedirectLink
                    key={`${vendorName}-vulnerabilities-${index}`}
                    to={ROUTES.VENDOR_PRODUCT({ page: null, vendorName, productName, ...({ year, month } as object) })}
                    label={normalizeName(productName)}
                    active={Boolean(productName)}
                />,
                cveCount
            ])
        ],
        [month, productsCveStore.list, year]
    );

    const handleYearSelect = useCallback(
        (year) => setPath({ page: null, year, month: year ? productsCveStore.month : null }),
        [productsCveStore, setPath]
    );

    const handleMonthSelect = useCallback((month) => setPath({ page: null, year: productsCveStore.year, month }), [
        productsCveStore,
        setPath
    ]);

    const handlePageChange = useCallback(
        (page) => setPath({ page, year: productsCveStore.year, month: productsCveStore.month }),
        [productsCveStore, setPath]
    );

    const handlePageSizeChange = useCallback(
        (pageSize) => setSearchParams({ pageSize: noneIf(pageSize, ProductsCveStore.DEFAULT_PAGE_SIZE) }),
        [setSearchParams]
    );

    return (
        <Layout
            ref={layoutRef}
            scrollable
            main={
                <>
                    <Heading
                        text={month || year ? `Vulnerabilities - ${monthName || ''} ${year || ''}` : 'Vulnerabilities'}
                    />
                    <YearFilter
                        selected={productsCveStore.year}
                        getOptionHref={getYearOptionHref}
                        onSelect={handleYearSelect}
                        className={classes.filter}
                    />
                    {year && (
                        <MonthFilter
                            selected={productsCveStore.month}
                            getOptionHref={getMonthOptionHref}
                            onSelect={handleMonthSelect}
                            className={classes.filter}
                        />
                    )}
                    <DataTable
                        data={tableData}
                        loading={productsCveStore.isLoading}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        getPaginationHref={getPaginationHref}
                        page={productsCveStore.page}
                        pageSize={productsCveStore.pageSize}
                        totalCount={productsCveStore.totalCount}
                    />
                </>
            }
        />
    );
};

export default observer(Years);
